import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams, Routes, Route } from 'react-router-dom';
import {
  RefreshCw, Search, Filter, Clock, User, Package,
  CheckCircle2, XCircle, Flame, Bell, ChevronRight,
  Phone, MapPin, MessageSquare, Image, X,
} from 'lucide-react';
import kitchenApi from '../services/kitchenApiService';

// ── Status config ─────────────────────────────────────────────────────────────
const TABS = [
  { value: 'new',       label: 'New',       color: '#3B82F6', bg: '#EFF6FF' },
  { value: 'accepted',  label: 'Accepted',  color: '#8B5CF6', bg: '#F5F3FF' },
  { value: 'preparing', label: 'Preparing', color: '#F59E0B', bg: '#FFFBEB' },
  { value: 'ready',     label: 'Ready',     color: '#10B981', bg: '#ECFDF5' },
  { value: 'completed', label: 'Completed', color: '#6B7280', bg: '#F1F5F9' },
];

const STATUS_ACTIONS = {
  new:       [{ label: 'Accept',   action: 'accepted',  style: 'success' }, { label: 'Reject', action: 'reject', style: 'danger' }],
  accepted:  [{ label: 'Start Prep', action: 'preparing', style: 'primary' }],
  preparing: [{ label: 'Mark Ready', action: 'ready',   style: 'success' }],
  ready:     [{ label: 'Complete',   action: 'completed', style: 'primary' }],
  completed: [],
};

const fmt$ = (n) => `$${(n || 0).toFixed(2)}`;

function OrderCard({ order, onAction, onView }) {
  const [actLoading, setActLoading] = useState(null);
  const [expanded, setExpanded]     = useState(false);
  const status = (order.status || 'new').toLowerCase();
  const tab    = TABS.find(t => t.value === status) || TABS[0];
  const actions = STATUS_ACTIONS[status] || [];

  const doAction = async (action) => {
    setActLoading(action);
    await onAction(order.order_id || order.id, action);
    setActLoading(null);
  };

  const items = order.items || order.order_items || [];
  const cookingRequests = items.filter(i => i.cooking_request || i.special_instructions);

  return (
    <div className="k-order-card">
      <div className="k-order-card-bar" style={{ background: tab.color }} />
      <div className="k-order-card-body">
        {/* Header row */}
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 12 }}>
          <div>
            <div style={{ fontSize: 14, fontWeight: 800, color: 'var(--k-primary)', fontFamily: 'monospace' }}>
              #{String(order.order_id || order.id).padStart(5, '0')}
            </div>
            <div style={{ fontSize: 12, color: 'var(--k-text-muted)', marginTop: 2 }}>
              {order.created_at ? new Date(order.created_at).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}
            </div>
          </div>
          <span className={`k-badge k-badge-${status}`}>{status}</span>
        </div>

        {/* Customer */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
          <div className="k-avatar-sm">{(order.customer_name || 'C').charAt(0).toUpperCase()}</div>
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--k-text)' }}>{order.customer_name || '—'}</div>
            {order.customer_phone && (
              <div style={{ fontSize: 12, color: 'var(--k-text-sub)', display: 'flex', alignItems: 'center', gap: 3 }}>
                <Phone size={11} /> {order.customer_phone}
              </div>
            )}
          </div>
          <div style={{ marginLeft: 'auto', textAlign: 'right' }}>
            <div style={{ fontSize: 16, fontWeight: 800, color: 'var(--k-text)' }}>{fmt$(order.total_amount || order.total)}</div>
            <div style={{ fontSize: 11, color: 'var(--k-text-sub)' }}>{items.length} item{items.length !== 1 ? 's' : ''}</div>
          </div>
        </div>

        {/* Slot time */}
        {order.slot_start_time && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, background: '#FFFBEB', border: '1px solid #FCD34D', borderRadius: 8, padding: '6px 10px', marginBottom: 10 }}>
            <Clock size={13} color="#D97706" />
            <span style={{ fontSize: 12, fontWeight: 600, color: '#D97706' }}>
              Slot: {new Date(order.slot_start_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </span>
          </div>
        )}

        {/* Items preview */}
        <div style={{ borderTop: '1px solid var(--k-border-light)', paddingTop: 10, marginBottom: 10 }}>
          {items.slice(0, expanded ? undefined : 3).map((item, idx) => (
            <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: 'var(--k-text)', marginBottom: 4 }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <span style={{ color: 'var(--k-text-sub)', fontWeight: 600, minWidth: 16 }}>{item.quantity}×</span>
                {item.item_name || item.name}
              </span>
              <span style={{ fontWeight: 600 }}>{fmt$(item.subtotal || item.total_price || (item.price * item.quantity))}</span>
            </div>
          ))}
          {items.length > 3 && (
            <button
              style={{ fontSize: 12, color: 'var(--k-primary)', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600, padding: '2px 0' }}
              onClick={() => setExpanded(p => !p)}
            >
              {expanded ? '▲ Show less' : `+${items.length - 3} more items`}
            </button>
          )}
        </div>

        {/* Cooking requests */}
        {cookingRequests.length > 0 && (
          <div style={{ background: '#FFF7F3', border: '1px solid #FED7AA', borderRadius: 8, padding: '8px 10px', marginBottom: 10 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--k-primary)', marginBottom: 4, display: 'flex', alignItems: 'center', gap: 4 }}>
              <MessageSquare size={12} /> Cooking Requests
            </div>
            {cookingRequests.map((item, i) => (
              <div key={i} style={{ fontSize: 12, color: '#92400E', marginBottom: 2 }}>
                <strong>{item.item_name || item.name}:</strong> {item.cooking_request || item.special_instructions}
              </div>
            ))}
          </div>
        )}

        {/* Special instructions */}
        {order.special_instructions && (
          <div style={{ background: '#F0FDF4', border: '1px solid #86EFAC', borderRadius: 8, padding: '8px 10px', marginBottom: 10, fontSize: 12, color: '#166534' }}>
            <strong>Note:</strong> {order.special_instructions}
          </div>
        )}

        {/* Actions */}
        <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
          {actions.map(({ label, action, style }) => (
            <button
              key={action}
              className={`k-btn k-btn-sm k-btn-${style}`}
              style={{ flex: 1 }}
              disabled={!!actLoading}
              onClick={() => doAction(action)}
            >
              {actLoading === action
                ? <span className="k-spinner k-spinner-sm" style={{ borderTopColor: style === 'danger' ? '#DC2626' : '#fff' }} />
                : label}
            </button>
          ))}
          <button
            className="k-btn k-btn-sm k-btn-ghost"
            style={{ padding: '6px 10px' }}
            onClick={() => onView(order.order_id || order.id)}
            title="View details"
          >
            <ChevronRight size={16} />
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main Orders List ──────────────────────────────────────────────────────────
function OrdersList() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab]       = useState('new');
  const [orders, setOrders]             = useState({});
  const [loading, setLoading]           = useState(true);
  const [refreshing, setRefreshing]     = useState(false);
  const [search, setSearch]             = useState('');
  const [counts, setCounts]             = useState({});

  const loadOrders = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    else setRefreshing(true);
    try {
      const results = await Promise.all(
        TABS.map(t => kitchenApi.getOrdersByStatus(t.value))
      );
      const newOrders = {};
      const newCounts = {};
      TABS.forEach((t, i) => {
        const data = results[i].success ? (results[i].data?.orders || results[i].data || []) : [];
        newOrders[t.value] = Array.isArray(data) ? data : [];
        newCounts[t.value] = newOrders[t.value].length;
      });
      setOrders(newOrders);
      setCounts(newCounts);
    } catch (e) { console.error(e); }
    finally { setLoading(false); setRefreshing(false); }
  }, []);

  useEffect(() => { loadOrders(); }, [loadOrders]);
  useEffect(() => {
    const id = setInterval(() => loadOrders(true), 20000);
    return () => clearInterval(id);
  }, [loadOrders]);

  const handleAction = async (orderId, action) => {
    let res;
    if (action === 'reject') {
      res = await kitchenApi.rejectOrder(orderId, 'Kitchen rejected order');
    } else {
      res = await kitchenApi.updateOrderStatus(orderId, action);
    }
    if (res.success) loadOrders(true);
    return res;
  };

  const currentOrders = orders[activeTab] || [];
  const filtered = search
    ? currentOrders.filter(o =>
        String(o.order_id || o.id).includes(search) ||
        (o.customer_name || '').toLowerCase().includes(search.toLowerCase())
      )
    : currentOrders;

  // Smart sort: overdue first, then by time
  const sorted = [...filtered].sort((a, b) => {
    const now  = Date.now();
    const aOv  = a.slot_start_time && new Date(a.slot_start_time).getTime() < now ? 1 : 0;
    const bOv  = b.slot_start_time && new Date(b.slot_start_time).getTime() < now ? 1 : 0;
    if (aOv !== bOv) return bOv - aOv;
    return new Date(a.slot_start_time || a.created_at || 0) - new Date(b.slot_start_time || b.created_at || 0);
  });

  const EMPTY = {
    new:       { icon: '🔔', title: 'No new orders', sub: 'New orders will appear here instantly' },
    accepted:  { icon: '✅', title: 'Nothing accepted yet', sub: 'Accept orders from the New tab' },
    preparing: { icon: '🍳', title: 'Kitchen is clear!', sub: 'Start preparing accepted orders' },
    ready:     { icon: '🏁', title: 'No orders ready', sub: 'Mark orders ready when done cooking' },
    completed: { icon: '🏆', title: 'No completed orders', sub: 'Completed orders appear here' },
  };

  return (
    <div>
      <div className="k-page-header">
        <div>
          <h1 className="k-page-title">Orders</h1>
          <p className="k-page-sub">Manage all kitchen orders in real time</p>
        </div>
        <button className="k-btn-icon" onClick={() => loadOrders()} disabled={refreshing} title="Refresh">
          <RefreshCw size={16} style={refreshing ? { animation: 'kSpin .8s linear infinite' } : {}} />
        </button>
      </div>

      {/* Search */}
      <div className="k-card" style={{ marginBottom: 16, padding: '12px 16px' }}>
        <div className="k-input-group" style={{ maxWidth: 360 }}>
          <span className="k-input-icon"><Search size={16} /></span>
          <input
            className="k-input"
            placeholder="Search order # or customer name…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
      </div>

      {/* Tabs */}
      <div className="k-tabs" style={{ marginBottom: 20 }}>
        {TABS.map(t => (
          <button
            key={t.value}
            className={`k-tab${activeTab === t.value ? ' active' : ''}`}
            onClick={() => setActiveTab(t.value)}
          >
            {t.label}
            {(counts[t.value] || 0) > 0 && (
              <span className="k-tab-count">{counts[t.value]}</span>
            )}
          </button>
        ))}
      </div>

      {/* Content */}
      {loading ? (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: 16 }}>
          {Array(4).fill(0).map((_, i) => (
            <div key={i} className="k-card">
              <div className="k-skeleton" style={{ height: 4, width: '100%', borderRadius: 2, marginBottom: 16 }} />
              {[80, 60, 40, 90, 50].map((w, j) => (
                <div key={j} className="k-skeleton" style={{ height: 14, width: `${w}%`, marginBottom: 10 }} />
              ))}
            </div>
          ))}
        </div>
      ) : sorted.length === 0 ? (
        <div className="k-card">
          <div className="k-empty">
            <div className="k-empty-icon">{EMPTY[activeTab]?.icon}</div>
            <div className="k-empty-title">{EMPTY[activeTab]?.title}</div>
            <div className="k-empty-sub">{EMPTY[activeTab]?.sub}</div>
          </div>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: 16 }}>
          {sorted.map(order => (
            <OrderCard
              key={order.order_id || order.id}
              order={order}
              onAction={handleAction}
              onView={(id) => navigate(`/kitchen/orders/${id}`)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ── Order Detail ──────────────────────────────────────────────────────────────
function OrderDetail() {
  const { orderId } = useParams();
  const navigate    = useNavigate();
  const [order, setOrder]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [actLoading, setActLoading] = useState(null);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      const res = await kitchenApi.getOrderDetails(orderId);
      if (res.success) setOrder(res.data?.order || res.data);
      setLoading(false);
    };
    load();
  }, [orderId]);

  const doAction = async (action) => {
    setActLoading(action);
    let res;
    if (action === 'reject') {
      res = await kitchenApi.rejectOrder(orderId, 'Kitchen rejected');
    } else {
      res = await kitchenApi.updateOrderStatus(orderId, action);
    }
    setActLoading(null);
    if (res.success) {
      const r = await kitchenApi.getOrderDetails(orderId);
      if (r.success) setOrder(r.data?.order || r.data);
    }
  };

  if (loading) return (
    <div className="k-loading-center">
      <div className="k-spinner" />
    </div>
  );
  if (!order) return (
    <div className="k-empty">
      <div className="k-empty-icon">❌</div>
      <div className="k-empty-title">Order not found</div>
      <button className="k-btn k-btn-primary" onClick={() => navigate('/kitchen/orders')} style={{ marginTop: 16 }}>Back to Orders</button>
    </div>
  );

  const status  = (order.status || 'new').toLowerCase();
  const actions = STATUS_ACTIONS[status] || [];
  const items   = order.items || order.order_items || [];

  return (
    <div style={{ maxWidth: 760, margin: '0 auto' }}>
      <div className="k-page-header">
        <div>
          <button
            className="k-btn k-btn-sm k-btn-ghost"
            onClick={() => navigate('/kitchen/orders')}
            style={{ marginBottom: 8, display: 'flex', alignItems: 'center', gap: 4 }}
          >
            ← Back
          </button>
          <h1 className="k-page-title">Order #{String(order.order_id || order.id).padStart(5, '0')}</h1>
          <p className="k-page-sub">{order.created_at ? new Date(order.created_at).toLocaleString() : ''}</p>
        </div>
        <span className={`k-badge k-badge-${status}`} style={{ fontSize: 14 }}>{status}</span>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 300px', gap: 20 }}>
        {/* Left — Items */}
        <div>
          <div className="k-card" style={{ marginBottom: 16 }}>
            <div className="k-card-title" style={{ marginBottom: 16 }}>Order Items</div>
            {items.map((item, i) => (
              <div key={i} style={{ display: 'flex', gap: 12, paddingBottom: 14, marginBottom: 14, borderBottom: i < items.length - 1 ? '1px solid var(--k-border-light)' : 'none' }}>
                <div style={{ width: 44, height: 44, background: 'var(--k-bg)', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, flexShrink: 0 }}>
                  🍛
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 700, fontSize: 14 }}>{item.item_name || item.name}</div>
                  <div style={{ fontSize: 12, color: 'var(--k-text-sub)', marginTop: 2 }}>Qty: {item.quantity} × {fmt$(item.price)}</div>
                  {(item.cooking_request || item.special_instructions) && (
                    <div style={{ marginTop: 6, background: '#FFF7F3', border: '1px solid #FED7AA', borderRadius: 6, padding: '5px 8px', fontSize: 12, color: '#92400E' }}>
                      🍳 <strong>Cooking request:</strong> {item.cooking_request || item.special_instructions}
                    </div>
                  )}
                  {item.modifiers?.length > 0 && (
                    <div style={{ marginTop: 4, fontSize: 12, color: 'var(--k-text-sub)' }}>
                      + {item.modifiers.map(m => m.name).join(', ')}
                    </div>
                  )}
                </div>
                <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--k-text)' }}>{fmt$(item.subtotal || item.total_price || (item.price * item.quantity))}</div>
              </div>
            ))}
            {/* Totals */}
            <div style={{ borderTop: '1px solid var(--k-border)', paddingTop: 12 }}>
              {order.subtotal && <div className="k-flex-between" style={{ marginBottom: 6, fontSize: 13, color: 'var(--k-text-sub)' }}><span>Subtotal</span><span>{fmt$(order.subtotal)}</span></div>}
              {order.tax_amount && <div className="k-flex-between" style={{ marginBottom: 6, fontSize: 13, color: 'var(--k-text-sub)' }}><span>Tax</span><span>{fmt$(order.tax_amount)}</span></div>}
              {order.delivery_fee && <div className="k-flex-between" style={{ marginBottom: 6, fontSize: 13, color: 'var(--k-text-sub)' }}><span>Delivery Fee</span><span>{fmt$(order.delivery_fee)}</span></div>}
              <div className="k-flex-between" style={{ fontSize: 16, fontWeight: 800, marginTop: 8 }}>
                <span>Total</span>
                <span style={{ color: 'var(--k-primary)' }}>{fmt$(order.total_amount || order.total)}</span>
              </div>
            </div>
          </div>

          {/* Special instructions */}
          {order.special_instructions && (
            <div className="k-card" style={{ marginBottom: 16 }}>
              <div className="k-card-title" style={{ marginBottom: 10 }}>Special Instructions</div>
              <p style={{ fontSize: 14, color: 'var(--k-text)', lineHeight: 1.6 }}>{order.special_instructions}</p>
            </div>
          )}

          {/* Actions */}
          {actions.length > 0 && (
            <div className="k-card">
              <div className="k-card-title" style={{ marginBottom: 12 }}>Actions</div>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                {actions.map(({ label, action, style }) => (
                  <button
                    key={action}
                    className={`k-btn k-btn-${style}`}
                    disabled={!!actLoading}
                    onClick={() => doAction(action)}
                  >
                    {actLoading === action
                      ? <span className="k-spinner k-spinner-sm" style={{ borderTopColor: '#fff' }} />
                      : label}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Right — Customer + Meta */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div className="k-card">
            <div className="k-card-title" style={{ marginBottom: 12 }}>Customer</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
              <div className="k-avatar">{(order.customer_name || 'C').charAt(0)}</div>
              <div>
                <div style={{ fontWeight: 700, fontSize: 14 }}>{order.customer_name || '—'}</div>
                {order.customer_phone && <div style={{ fontSize: 12, color: 'var(--k-text-sub)' }}>{order.customer_phone}</div>}
              </div>
            </div>
            {order.delivery_address && (
              <div style={{ display: 'flex', gap: 8, fontSize: 13, color: 'var(--k-text)' }}>
                <MapPin size={14} style={{ flexShrink: 0, marginTop: 1, color: 'var(--k-text-muted)' }} />
                <span>{typeof order.delivery_address === 'string' ? order.delivery_address : [order.delivery_address.line1, order.delivery_address.city].filter(Boolean).join(', ')}</span>
              </div>
            )}
          </div>

          <div className="k-card">
            <div className="k-card-title" style={{ marginBottom: 12 }}>Order Info</div>
            {[
              { label: 'Payment', value: order.payment_method || '—' },
              { label: 'Payment Status', value: order.payment_status || '—' },
              { label: 'Order Type', value: order.order_type || '—' },
              { label: 'Slot Time', value: order.slot_start_time ? new Date(order.slot_start_time).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—' },
            ].map(({ label, value }) => (
              <div key={label} className="k-flex-between" style={{ marginBottom: 8, fontSize: 13 }}>
                <span style={{ color: 'var(--k-text-sub)' }}>{label}</span>
                <span style={{ fontWeight: 600, textTransform: 'capitalize' }}>{value}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Router wrapper ────────────────────────────────────────────────────────────
export default function OrdersPage() {
  return (
    <Routes>
      <Route index element={<OrdersList />} />
      <Route path=":orderId" element={<OrderDetail />} />
    </Routes>
  );
}
